/**
 * Fly-and-Swap (house gear) availability and the shared wording that depends
 * on it.
 *
 * The fleet does not physically exist until the venue opens, so today every
 * house-gear request is recorded as *interest only*: it never seats a pilot,
 * never consumes a loaner, and never triggers gear promises in email.
 *
 * Flip `HOUSE_GEAR_AVAILABLE` to `true` on venue-live day and the option
 * becomes real everywhere — RSVP form, seating, emails, marketing labels.
 */

import type { VideoProtocol } from "@/lib/rf-grid";

export const HOUSE_GEAR_AVAILABLE = false;

/** The pilot explicitly asked to fly house gear. */
export function wantsHouseGear(protocol: VideoProtocol | null | undefined): boolean {
  return protocol === "FLY_AND_SWAP";
}

/** We have no idea what they're flying yet — never assume house gear. */
export function gearUnknown(protocol: VideoProtocol | null | undefined): boolean {
  return !protocol;
}

/**
 * House gear only consumes fleet capacity once the fleet exists. Until then a
 * request is interest we record, not a promise we made.
 */
export function houseGearIsBookable(protocol: VideoProtocol | null | undefined): boolean {
  return HOUSE_GEAR_AVAILABLE && wantsHouseGear(protocol);
}

export const HOUSE_GEAR_OPTION_LABEL = HOUSE_GEAR_AVAILABLE
  ? "I need a house rental"
  : "I'd want house gear (coming with the venue)";

export const HOUSE_GEAR_COMING_SOON_NOTE =
  "Fly-and-Swap house gear launches with the venue. For now these nights are bring-your-own-gear — pick this and we'll count you in when the fleet lands.";

/** Shown to a pilot flying their own gear: they're the ones who set a VTX. */
export const BYO_FREQ_NOTE =
  "We hold a raceband for you — set your VTX to the channel we send before you fly.";

/** Shown to a house-gear pilot: we control that hardware, no channel to set. */
export const HOUSE_GEAR_READY_NOTE = HOUSE_GEAR_AVAILABLE
  ? "We'll have a quad ready for you — nothing to set, we hand it over on the right channel."
  : HOUSE_GEAR_COMING_SOON_NOTE;

/** Shown when we still don't know what they fly — ask, never assume. */
export const GEAR_UNKNOWN_NOTE =
  "Tell us what video system you're flying and we'll hold a raceband for you — you can add it any time from your pilot profile.";

/** Crew-facing roster labels. */
export const GEAR_UNKNOWN_BADGE = "Gear unknown";
export const HOUSE_GEAR_INTEREST_BADGE = HOUSE_GEAR_AVAILABLE
  ? "House gear"
  : "Wants house gear (interest)";

/** What SquadFlight calls its house fleet, with the class it serves. */
export const FLY_AND_SWAP_FLEET_PREFIX = "Fly & Swap Fleet";

/**
 * What's in the take-home kit. One source of truth so the session page and the
 * emails describe the same box.
 */
export const KIT_INCLUDES_NOTE =
  "The same rig you fly tonight: quad, controller, goggles, batteries and charger — bind-and-fly, picked up in person.";

/**
 * The class portion of a Proving Ground label, read from the event title
 * ("SquadFlight | Proving Ground | Level 0 Certification" → LVL0, the Intro
 * class → Intro). Falls back to the program name when the title is ambiguous.
 */
export function provingGroundClassLabel(title: string | null | undefined): string {
  const t = (title ?? "").toUpperCase();
  if (/LVL\s*0|LEVEL\s*0/.test(t)) return "LVL0";
  if (/INTRO/.test(t)) return "Intro";
  return "Proving Ground";
}

/** Shape of the event facts the label needs. */
export type GearEventContext = {
  event_type?: string | null;
  title?: string | null;
};

/** Proving Ground is the entry-level program — SquadFlight provides the gear. */
export function isProvingGroundEvent(event?: GearEventContext | null): boolean {
  if (!event) return false;
  if (event.event_type === "proving_ground") return true;
  return /PROVING\s*GROUND/i.test(event.title ?? "");
}

/** One line describing the gear state of an RSVP, for rosters and CSV. */
export function gearStateLabel(protocol: VideoProtocol | null | undefined): string {
  if (wantsHouseGear(protocol)) return HOUSE_GEAR_INTEREST_BADGE;
  if (gearUnknown(protocol)) return GEAR_UNKNOWN_BADGE;
  return protocol as string;
}

/**
 * Same, but aware of what kind of night it is. On a Proving Ground class
 * everyone is expected on house gear — an unknown VTX there isn't a mystery,
 * it's the Fly & Swap Fleet doing its job. Higher-level classes can still be
 * bring-your-own, so they keep the honest "Gear unknown".
 */
export function gearStateLabelForEvent(
  protocol: VideoProtocol | null | undefined,
  event?: GearEventContext | null,
): string {
  if (!isProvingGroundEvent(event)) return gearStateLabel(protocol);
  const fleet = `${FLY_AND_SWAP_FLEET_PREFIX} | ${provingGroundClassLabel(event?.title)}`;
  if (wantsHouseGear(protocol) || gearUnknown(protocol)) return fleet;
  return protocol as string;
}

/** Guidance paragraph for RSVP email, keyed on what we know about their gear. */
export function gearNoteFor(protocol: VideoProtocol | null | undefined): string {
  if (wantsHouseGear(protocol)) return HOUSE_GEAR_READY_NOTE;
  if (gearUnknown(protocol)) return GEAR_UNKNOWN_NOTE;
  return BYO_FREQ_NOTE;
}
